import { type } from 'arktype';

const MAX_PROFILE_IMAGE_DATA_URI_LENGTH = 1_400_000;

/**
 * Schema for updating a user's direct role assignments.
 */
export const updateUserRolesSchema = type({
  roleIds: type('string[]').configure({
    description: 'Full replacement set of role IDs assigned directly to the user — anything not listed is removed.',
    examples: [['manager', 'planner']],
  }),
}).configure({ description: 'Payload to replace the complete set of direct roles on a user.' });

export type UpdateUserRoles = typeof updateUserRolesSchema.t;

/**
 * Schema for updating a user's activation status (activate/deactivate).
 */
export const updateUserStatusSchema = type({
  active: type('boolean').configure({
    description: 'New activation flag: true to activate, false to ban/deactivate the user.',
    examples: [true, false],
  }),
}).configure({ description: 'Payload to activate or deactivate a user account.' });

export type UpdateUserStatus = typeof updateUserStatusSchema.t;

/**
 * Schema for forcing (or clearing) the must-change-password flag on a user.
 */
export const setMustChangePasswordSchema = type({
  mustChangePassword: type('boolean').configure({
    description: 'true to force the user to change their password on next login, false to clear the flag.',
    examples: [true, false],
  }),
}).configure({ description: 'Payload to set or clear the must-change-password flag.' });

export type SetMustChangePassword = typeof setMustChangePasswordSchema.t;

/**
 * Schema for linking a person to a user.
 */
export const linkPersonSchema = type({
  personId: type('string').configure({
    description: 'ID of the person record to link to this user.',
    examples: ['per_abc123'],
  }),
}).configure({ description: 'Payload to link a user account to an existing person record.' });

export type LinkPerson = typeof linkPersonSchema.t;

/**
 * Role summary attached to users.
 */
export const userRoleSchema = type({
  id: type('string').configure({
    description: 'Semantic role ID.',
    examples: ['manager', 'team-lead'],
  }),
  'description?': type('string | null').configure({
    description: 'Human-readable role description. Optional and nullable.',
    examples: ['Manages a single team'],
  }),
  'createdAt?': type('string | Date').configure({
    description: 'ISO 8601 timestamp of when the role was created. Optional.',
    examples: ['2026-04-22T08:15:30.000Z'],
  }),
  'updatedAt?': type('string | Date').configure({
    description: 'ISO 8601 timestamp of when the role was last updated. Optional.',
    examples: ['2026-04-22T08:15:30.000Z'],
  }),
}).configure({ description: 'Role summary embedded inside a user payload.' });

export type UserRole = typeof userRoleSchema.t;

/**
 * Group membership summary attached to users.
 */
export const userGroupSchema = type({
  id: type('string').configure({
    description: 'Group ID.',
    examples: ['grp_abc123'],
  }),
  name: type('string').configure({
    description: 'Group name.',
    examples: ['Planning Team'],
  }),
  'description?': type('string | null').configure({
    description: 'Group description. Optional and nullable.',
    examples: ['Coordinates shift planning across nursing teams'],
  }),
  'createdAt?': type('string | Date').configure({
    description: 'ISO 8601 timestamp of when the group was created. Optional.',
    examples: ['2026-04-22T08:15:30.000Z'],
  }),
  'updatedAt?': type('string | Date').configure({
    description: 'ISO 8601 timestamp of when the group was last updated. Optional.',
    examples: ['2026-04-22T08:15:30.000Z'],
  }),
  isAdmin: type('boolean').configure({
    description: 'Whether the user is an admin of this group.',
    examples: [false, true],
  }),
}).configure({ description: 'Group membership entry embedded inside a user payload.' });

export type UserGroup = typeof userGroupSchema.t;

/**
 * Permission summary attached to users.
 */
export const userPermissionSchema = type({
  id: type('string').configure({
    description: 'Permission ID in "resource:action" (or hierarchical) form.',
    examples: ['persons:read', 'persons:profile:phone:write'],
  }),
  'description?': type('string | null').configure({
    description: 'Human-readable description of the permission. Optional and nullable.',
    examples: ['Read access to person records'],
  }),
  'createdAt?': type('string | Date').configure({
    description: 'ISO 8601 timestamp of when the permission was registered. Optional.',
    examples: ['2026-04-22T08:15:30.000Z'],
  }),
}).configure({ description: 'Permission summary embedded inside a user payload.' });

export type UserPermission = typeof userPermissionSchema.t;

/**
 * Person summary linked to a user.
 */
export const linkedPersonSchema = type({
  id: type('string').configure({
    description: 'Person record ID.',
    examples: ['per_abc123'],
  }),
  firstName: type('string').configure({
    description: 'Given name of the linked person.',
    examples: ['Anna'],
  }),
  lastName: type('string').configure({
    description: 'Family name of the linked person.',
    examples: ['Müller'],
  }),
}).configure({ description: 'Summary of the person record linked to a user account.' });

export type LinkedPerson = typeof linkedPersonSchema.t;

/**
 * Group available for assignment (not yet assigned to user).
 */
export const unassignedGroupSchema = type({
  id: type('string').configure({
    description: 'Group ID.',
    examples: ['grp_abc123'],
  }),
  name: type('string').configure({
    description: 'Group name.',
    examples: ['Planning Team'],
  }),
  'description?': type('string | null').configure({
    description: 'Group description. Optional and nullable.',
    examples: ['Coordinates shift planning across nursing teams'],
  }),
}).configure({ description: 'Group entry shown in the "available to assign" picker for a user.' });

export type UnassignedGroup = typeof unassignedGroupSchema.t;

/**
 * Person available for linking (not yet linked to any user).
 */
export const unlinkedPersonSchema = type({
  id: type('string').configure({
    description: 'Person record ID.',
    examples: ['per_abc123'],
  }),
  firstName: type('string').configure({
    description: 'Given name of the person.',
    examples: ['Anna'],
  }),
  lastName: type('string').configure({
    description: 'Family name of the person.',
    examples: ['Müller'],
  }),
}).configure({ description: 'Person entry shown in the "available to link" picker for a user.' });

export type UnlinkedPerson = typeof unlinkedPersonSchema.t;

export interface ManagedUser {
  id: string;
  name: string;
  email: string;
  username?: string | null;
  image: string | null;
  createdAt: string | Date;
  banned: boolean;
  mustChangePassword: boolean;
  directRoles: UserRole[];
  groups: UserGroup[];
  linkedPerson: LinkedPerson | null;
}

export interface ManagedUserDetails extends ManagedUser {
  inheritedRoles: UserRole[];
  effectivePermissions: UserPermission[];
}

/**
 * Response shape for `GET /api/v1/rbac/users`.
 *
 * Since the v1 freeze, single-resource endpoints return the object directly
 * (no `{data}` wrapper). Collections with pagination return `{data, meta}`
 * via `createListResponse()`. This list is small and bounded, so it ships as
 * a raw array — consistent with the other non-paginated admin lists until
 * they are moved to `createListResponse` in a future phase.
 */
export type UsersResponse = ManagedUser[];

/**
 * Response shape for `GET /api/v1/users/me`, `GET /api/v1/rbac/users/:id`,
 * `PUT /api/v1/rbac/users/:id/roles`, and every other single-resource
 * user-details endpoint. Direct object — no `{data}` wrapper.
 */
export type UserDetailsResponse = ManagedUserDetails;

/**
 * User's effective permissions snapshot used by route guards and UI checks.
 */
export const userPermissionsResponseSchema = type({
  data: type({
    roles: type('string[]').configure({
      description: 'Effective role IDs held by the user (direct + inherited via groups).',
      examples: [['manager', 'planner']],
    }),
    permissions: type('string[]').configure({
      description: 'Effective permission IDs granted to the user via all roles combined.',
      examples: [['persons:read', 'persons:write']],
    }),
  }).configure({ description: 'Effective-access snapshot used by route guards and UI checks.' }),
}).configure({ description: 'Wrapper payload returned by the permissions-snapshot endpoint.' });

export type UserPermissionsResponse = typeof userPermissionsResponseSchema.t;

/**
 * Own profile update payload.
 */
export const updateProfileSchema = type({
  'name?': type('string').configure({
    description: 'Updated display name. Optional.',
    examples: ['Anna Müller'],
  }),
  'image?': type(`string <= ${MAX_PROFILE_IMAGE_DATA_URI_LENGTH} | null`).configure({
    description:
      'Updated profile image URL or data URI. Pass null to clear. Optional and nullable. Max 1 MB raw image size (~1.4 million characters as a base64 data URI) — matches the client-side avatar upload limit.',
    examples: ['https://cdn.example.ch/avatars/anna.png', null],
  }),
  'preferredLanguage?': type('string | null').configure({
    description: 'IETF language tag for preferred UI language. Pass null to reset to tenant default. Optional and nullable.',
    examples: ['de-CH', 'en', null],
  }),
  'preferredTheme?': type('string | null').configure({
    description: "UI theme preference ('light', 'dark', or 'system'). Pass null to reset to tenant default. Optional and nullable.",
    examples: ['light', 'dark', null],
  }),
}).configure({ description: 'Payload used by a signed-in user to update their own profile.' });
export type UpdateProfile = typeof updateProfileSchema.t;

/**
 * Password change payload.
 */
export const changePasswordSchema = type({
  currentPassword: type('string').configure({
    description: 'Current plaintext password, used to re-authenticate before changing.',
    examples: ['old-correct-horse-battery-staple'],
  }),
  newPassword: type('string').configure({
    description: 'New plaintext password meeting the tenant password policy.',
    examples: ['new-correct-horse-battery-staple'],
  }),
  'revokeOtherSessions?': type('boolean').configure({
    description: "When true, all of this user's other active sessions are revoked on success. Optional.",
    examples: [true, false],
  }),
}).configure({ description: 'Payload for a signed-in user to change their own password.' });
export type ChangePassword = typeof changePasswordSchema.t;

/**
 * Set password payload (for social accounts).
 */
export const setPasswordSchema = type({
  newPassword: type('string').configure({
    description: 'New plaintext password to attach to the account. Must meet the tenant password policy.',
    examples: ['correct-horse-battery-staple'],
  }),
}).configure({ description: 'Payload to set an initial password on a user account that was previously social-only.' });
export type SetPassword = typeof setPasswordSchema.t;

const loginAccountFields = {
  'username?': type('string').configure({
    description: 'Username for a username-mode account. Defaults to the email in username mode.',
    examples: ['anna.mueller'],
  }),
  'password?': type('string >= 8').configure({
    description:
      'Initial plaintext password, min 8. Optional; ignored when the email links an existing user. User will be required to change it on first login.',
    examples: ['Welc0me!2026'],
  }),
  'roleIds?': type('string[]').configure({
    description: 'Role IDs to assign to the login account. Optional — defaults to no direct roles.',
    examples: [['employee']],
  }),
  'groupIds?': type('string[]').configure({
    description: 'Group IDs to assign to the login account. Optional — defaults to no groups.',
    examples: [['grp_abc123']],
  }),
};

/** Login account nested in a person-creation payload. */
export const personUserSchema = type({
  email: type('string.email').configure({
    description: 'Email address for the new login account.',
    examples: ['anna.mueller@example.ch'],
  }),
  ...loginAccountFields,
}).configure({ description: 'Login account created together with a person record.' });

export type PersonUserInput = typeof personUserSchema.t;

/**
 * Schema for activating a new user account directly from the person master data.
 *
 * Creates an auth.users + auth.accounts (credential) record, links persons.userId,
 * and assigns the specified roles and groups — all in a single transaction.
 */
export const activateUserFromPersonSchema = type({
  'email?': type('string.email').configure({
    description: 'Email address for an email-mode account.',
    examples: ['anna.mueller@example.ch'],
  }),
  ...loginAccountFields,
}).configure({
  description:
    'Payload to directly activate a person as a login user from the person master data. The active tenant credential mode determines the required identifier.',
});

export type ActivateUserFromPerson = typeof activateUserFromPersonSchema.t;

export interface SelfProfile {
  id: string;
  name: string;
  email: string;
  /**
   * The effective avatar to display. When a person is linked, this is the person's
   * profile picture (person wins). Otherwise it is the user's own image.
   */
  image: string | null;
  preferredLanguage: string | null;
  preferredTheme: string | null;
  /** When true, the user must change their password before accessing the app. */
  mustChangePassword: boolean;
  linkedPerson: {
    id: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string | null;
    nationality: string | null;
    sex: 'male' | 'female' | 'other' | 'unspecified' | null;
    /** Profile picture stored on the linked person record (core.persons.profile_picture). */
    profilePicture: string | null;
    departments: string[];
    jobRoles: string[];
    teams: string[];
    locations: Array<{
      id: string;
      locationId: string;
      type: string;
      locationName: string;
      locationType: 'physical' | 'virtual';
    }>;
  } | null;
}

export interface LinkedAccount {
  id: string;
  providerId: string;
  accountId: string;
  createdAt: Date | string;
}

/**
 * Schema for creating an invitation.
 */
export const createInvitationSchema = type({
  email: type('string').configure({
    description: 'Email address to invite.',
    examples: ['anna.mueller@example.ch'],
  }),
  'name?': type('string').configure({
    description: 'Optional pre-filled display name for the invitee.',
    examples: ['Anna Müller'],
  }),
  'roleIds?': type('string[]').configure({
    description: 'Roles granted to the new user on acceptance. Optional — defaults to no direct roles.',
    examples: [['employee']],
  }),
  'message?': type('string').configure({
    description: 'Optional custom message included in the invitation email.',
    examples: ['Welcome to the team!'],
  }),
}).configure({ description: 'Payload to create a single invitation.' });

export type CreateInvitation = typeof createInvitationSchema.t;

/**
 * Invitation response from API
 */
export interface InvitationResponse {
  id: string;
  email: string;
  name?: string | null;
  inviterName: string;
  status: 'pending' | 'accepted' | 'revoked';
  roleIds: string[];
  message?: string | null;
  createdAt: Date | string;
}

/**
 * Invitation validation response for public endpoint
 */
export interface InvitationValidationResponse {
  email: string;
  /** Pre-filled name from the invitation (set by the admin when inviting) */
  inviteeName?: string | null;
  inviterName: string;
  message?: string | null;
  availableLoginMethods: {
    emailPassword: boolean;
    socialProviders: string[];
  };
  existingAccount: boolean;
}

/**
 * Schema for accepting an invitation with password
 */
export const acceptInvitationSchema = type({
  token: type('string').configure({
    description: 'Invitation token from the acceptance link.',
    examples: ['inv_tok_abc123xyz'],
  }),
  name: type('string').configure({
    description: 'Display name the invitee wants to use.',
    examples: ['Anna Müller'],
  }),
  password: type('string').configure({
    description: 'Plaintext password meeting the tenant password policy.',
    examples: ['correct-horse-battery-staple'],
  }),
}).configure({ description: 'Payload to accept an invitation and create the new user account.' });

export type AcceptInvitation = typeof acceptInvitationSchema.t;

// ---------------------------------------------------------------------------
// Batch Invitations
// ---------------------------------------------------------------------------

/**
 * Schema for batch invitation requests.
 * Supports inviting up to 100 users in a single operation.
 */
export const createBatchInvitationsSchema = type({
  invitations: type({
    email: type('string.email').configure({
      description: 'Email address to invite.',
      examples: ['anna.mueller@example.ch'],
    }),
    'name?': type('string').configure({
      description: 'Optional pre-filled display name for the invitee.',
      examples: ['Anna Müller'],
    }),
  })
    .array()
    .atLeastLength(1)
    .atMostLength(100)
    .configure({ description: 'Array of 1-100 invitation entries to send in this batch.' }),
  roleIds: type('string[]').configure({
    description: 'Roles granted to every new user on acceptance (applies to the whole batch).',
    examples: [['employee']],
  }),
  'message?': type('string').configure({
    description: 'Optional custom message included in every invitation email in the batch.',
    examples: ['Welcome to the team!'],
  }),
}).configure({ description: 'Payload to send up to 100 invitations in a single operation.' });

export type CreateBatchInvitations = typeof createBatchInvitationsSchema.t;

export interface BatchInvitationSuccessItem {
  email: string;
  invitationId: string;
}

export interface BatchInvitationFailureItem {
  email: string;
  reason: string;
}

export interface BatchInvitationResult {
  succeeded: BatchInvitationSuccessItem[];
  failed: BatchInvitationFailureItem[];
  totalSent: number;
  totalFailed: number;
}
